import { Controller } from "@hotwired/stimulus";
import intlTelInput from "intl-tel-input/intlTelInputWithUtils";
import "intl-tel-input/build/css/intlTelInput.css";
import { defaultPhoneCountry } from "../utils/default_phone_country";

export default class extends Controller {
  static targets = ["input"];
  // Nom de l'évènement de fenêtre à émettre pour publier le numéro complet.
  // Utile hors formulaire : une modale portalisée dans <body> n'a rien à
  // soumettre, elle doit rendre sa saisie à qui l'écoute.
  // Restricts the country list (e.g. ["cm"] for mobile money, Cameroon-only).
  // `examplePlaceholder: false` keeps the field empty of any sample number — the
  // GO4LEARN checkout forbids placeholders ("an example is a disguised default").
  static values = {
    emitEvent: String,
    onlyCountries: Array,
    examplePlaceholder: { type: Boolean, default: true },
    // Where the country list opens (a CSS selector), <body> by default. A native modal
    // <dialog> lives in the top layer: a list appended to <body> would open behind it.
    dropdownContainer: String,
  };

  connect() {
    const hasExistingValue = this.inputTarget.value.trim() !== "";
    // A field may name its country (data-default-country="cm"): the mobile
    // money number of a Cameroonian school started on the visitor's own
    // country, guessed from the IP address (QA round 3, PAR-15).
    const defaultCountry = this.inputTarget.dataset.defaultCountry;

    const only = this.hasOnlyCountriesValue ? this.onlyCountriesValue : [];

    this.iti = intlTelInput(this.inputTarget, {
      ...(only.length > 0 ? { onlyCountries: only } : {}),
      // A single allowed country, else the field's own country, else the
      // school's inside its space, else the visitor's.
      initialCountry:
        only.length === 1
          ? only[0]
          : hasExistingValue
            ? ""
            : defaultPhoneCountry(document, defaultCountry),
      geoIpLookup: (callback) => {
        fetch("https://ipapi.co/json/")
          .then((res) => res.json())
          .then((data) => callback(data.country_code))
          .catch(() => callback("cm"));
      },
      countryOrder: ["cm", "fr"],
      nationalMode: true,
      ...(this.examplePlaceholderValue ? {} : { autoPlaceholder: "off" }),
      formatOnDisplay: true,
      // La liste des pays est posée dans <body> plutôt que dans le champ :
      // sinon le moindre conteneur à overflow — une modale, une carte à
      // défilement — la découpe. La bibliothèque la repositionne elle-même.
      dropdownContainer:
        (this.hasDropdownContainerValue &&
          this.element.closest(this.dropdownContainerValue)) ||
        document.body,
    });

    // Make the intl-tel-input container full width
    const container = this.inputTarget.closest(".iti");
    if (container) {
      container.style.width = "100%";
    }

    // La bibliothèque réserve la place du drapeau par un padding-left EN LIGNE.
    // Or plusieurs feuilles du projet imposent le leur en !important — ainsi
    // « .public-site .g4s-input { padding: 13px 15px !important } » — et un
    // !important de feuille bat un style en ligne : le retrait disparaissait et
    // le drapeau passait sur le texte. On repose donc la valeur avec la même
    // priorité, en refaisant le calcul de la bibliothèque (largeur du sélecteur
    // + 6 px) plutôt qu'en courant après le moment où elle l'applique.
    this.enforcePadding = () => {
      const selected = container?.querySelector(".iti__selected-country");
      const width = selected?.offsetWidth || 42;

      this.inputTarget.style.setProperty(
        "padding-left",
        `${width + 6}px`,
        "important",
      );
    };
    this.enforcePadding();
    this.inputTarget.addEventListener("countrychange", this.enforcePadding);

    this.form = this.inputTarget.closest("form");
    if (this.form) {
      this.submitHandler = (e) => this.handleSubmit(e);
      this.form.addEventListener("submit", this.submitHandler);
    }
  }

  handleSubmit(e) {
    const fullNumber = this.iti.getNumber();
    if (fullNumber) {
      this.inputTarget.value = fullNumber;
    }
  }

  // Publie le numéro au format international (+237…), quel que soit ce que
  // l'utilisateur a tapé : c'est l'indicatif choisi dans la liste qui fait foi.
  emitNumber() {
    if (!this.hasEmitEventValue) return;

    window.dispatchEvent(
      new CustomEvent(this.emitEventValue, {
        detail: { value: this.iti.getNumber() || this.inputTarget.value },
      }),
    );
  }

  disconnect() {
    if (this.form && this.submitHandler) {
      this.form.removeEventListener("submit", this.submitHandler);
    }
    if (this.enforcePadding) {
      this.inputTarget.removeEventListener("countrychange", this.enforcePadding);
    }
    if (this.iti) {
      this.iti.destroy();
    }
  }
}
